import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Modal,
  Grid,
  Typography,
  MenuItem,
} from "@mui/material";
import ModalHeader from "../modals/ModalHeader";
import Spinner from "./loading-screen/Spinner";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import ApiEndpoints from "../network/ApiEndPoints";
import { get, postJsonData } from "../network/ApiController";
import ViewModal from "../modals/ViewModal";
import ModalFooter from "../modals/ModalFooter";
import { fileToBase64, splitIntoFlatChunks } from "../utils/ImageUtil";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  p: 2,
  height: "max-content",
  overflowY: "scroll",
};

const EditProject = ({ open, onClose, data, refresh }) => {
  const [request, setRequest] = useState(false);
  const [priority, setPriority] = useState("Medium");
  const [updatedProject, setUpdatedProject] = useState({
    name: "",
    start_date: "",
    end_date: "",
    budget: "",
    priority: "",
    status: "",
    project_image: null,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [statusList, setStatusList] = useState([]);

  useEffect(() => {
    if (open && data) {
      setUpdatedProject({
        name: data?.name || "",
        start_date: data?.start_date || "",
        end_date: data?.end_date || "",
        budget: data?.budget || "",
        priority: data?.priority || "Medium",
        status: data?.status || "OPEN",
        project_image: null,
      });

      setImagePreview(data?.project_image || null);
    }
  }, [open, data]);

  useEffect(() => {
    get(
      `${ApiEndpoints.GET_UTILS}?type=ProjectStatus`,
      "",
      setRequest,
      (res) => {
        if (res?.data?.data) {
          setStatusList(res.data.data);
        }
      },
      apiErrorToast
    );
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProject({
      ...updatedProject,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setUploadedImage(file);
      setUpdatedProject({
        ...updatedProject,
        project_image: file,
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setRequest(true);

    try {
      const updateProjectUrl = `${ApiEndpoints.UPDATE_PROJECT}/${data.id}`;

      // 1️⃣ Prepare normal fields
      const payload = {};
      Object.keys(updatedProject).forEach((key) => {
        if (key !== "project_image") {
          payload[key] = updatedProject[key];
        }
      });

      // 2️⃣ If image exists → convert & chunk
      if (uploadedImage) {
        const base64 = await fileToBase64(uploadedImage);
        const imageChunks = splitIntoFlatChunks(base64, "project_image");

        // Merge chunks into payload
        Object.assign(payload, imageChunks);
      }

      // 3️⃣ Send JSON payload
      postJsonData(
        updateProjectUrl,
        payload,
        setRequest,
        () => {
          okSuccessToast("Project updated successfully");
          onClose();
          refresh();
        },
        apiErrorToast
      );
    } catch (err) {
      console.error(err);
      apiErrorToast("Image upload failed");
    } finally {
      setRequest(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <ModalHeader
          title="Edit Project"
          handleClose={onClose}
          subtitle="Edit the details of your project"
        />
        <Spinner loading={request} />
        <Box
          component="form"
          id="EditProject"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <TextField
                label="Project Name"
                name="name"
                value={updatedProject?.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                name="start_date"
                type="date"
                value={updatedProject?.start_date}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                name="end_date"
                type="date"
                value={updatedProject?.end_date}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Budget"
                name="budget"
                type="number"
                value={updatedProject?.budget}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Priority"
                name="priority"
                value={updatedProject?.priority}
                onChange={handleChange}
                fullWidth
                required
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="Status"
                name="status"
                value={updatedProject.status}
                onChange={handleChange}
                fullWidth
                required
              >
                {statusList.map((item) => (
                  <MenuItem key={item.id} value={item.name}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography>Upload New Project Image</Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ width: "100%", marginBottom: "10px" }}
              />

              {!uploadedImage && data?.project_image && (
                <Box sx={{ mb: 2 }}>
                  <ViewModal
                    row={data}
                    title="View Project Image"
                    fileKey="project_image"
                  />
                </Box>
              )}

              {uploadedImage && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={imagePreview}
                    alt="New Preview"
                    width="100"
                    height="100"
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <ModalFooter form="EditProject" request={request} btn="Update" />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditProject;
